import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, storage } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";

export default function Profile() {
  const navigate = useNavigate();
  const { lang, lp, t } = useLang();
  const [loading, setLoading] = useState(false);
  const [contexts, setContexts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    speaking_context: "",
    main_goal: "",
  });

  useEffect(() => {
    api.getSeedData(lang).then(data => {
      setContexts(data.speaking_contexts);
    }).catch(console.error);

    const existingUser = storage.getUser();
    if (existingUser) {
      setFormData({
        name: existingUser.name || "",
        email: existingUser.email || "",
        speaking_context: existingUser.speaking_context || "",
        main_goal: existingUser.main_goal || "",
      });
    }
  }, [lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t.profile.errEnterName);
      return;
    }

    if (!formData.email.trim()) {
      toast.error(t.profile.errEnterEmail);
      return;
    }

    setLoading(true);
    try {
      const user = await api.createOrGetUser(formData);
      storage.setUser(user);
      storage.clearAssessmentProgress();
      toast.success(t.profile.saved);
      navigate(lp("/assessment/1"));
    } catch (error) {
      console.error(error);
      toast.error(t.profile.genericError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#222222] text-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(lp("/"))}
          className="text-white/60 hover:text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.back}
        </Button>
        <div className="flex items-center gap-4">
          <p className="text-sm text-white/60 hidden sm:block">{t.branding}</p>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="px-6 py-12 max-w-xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Icon and Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#FFEA00]/10 flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-[#FFEA00]" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t.profile.title}
            </h1>
            <p className="text-white/60">
              {t.profile.subtitle}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">{t.profile.nameLabel}</Label>
              <Input
                id="name"
                data-testid="name-input"
                placeholder={t.profile.namePlaceholder}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#333] border-white/10 text-white placeholder:text-white/40 focus:border-[#FFEA00] focus:ring-[#FFEA00]"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{t.profile.emailLabel}</Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder={t.profile.emailPlaceholder}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#333] border-white/10 text-white placeholder:text-white/40 focus:border-[#FFEA00] focus:ring-[#FFEA00]"
                required
              />
              <p className="text-xs text-white/50">{t.profile.consent}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="context" className="text-white">{t.profile.contextLabel}</Label>
              <Select
                value={formData.speaking_context}
                onValueChange={(value) => setFormData({ ...formData, speaking_context: value })}
              >
                <SelectTrigger
                  data-testid="context-select"
                  className="bg-[#333] border-white/10 text-white focus:border-[#FFEA00] focus:ring-[#FFEA00]"
                >
                  <SelectValue placeholder={t.profile.contextPlaceholder} />
                </SelectTrigger>
                <SelectContent className="bg-[#333] border-white/10">
                  {contexts.map((context) => (
                    <SelectItem key={context} value={context} className="text-white hover:bg-white/10">
                      {context}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="goal" className="text-white">{t.profile.goalLabel}</Label>
              <Textarea
                id="goal"
                data-testid="goal-input"
                placeholder={t.profile.goalPlaceholder}
                value={formData.main_goal}
                onChange={(e) => setFormData({ ...formData, main_goal: e.target.value })}
                className="bg-[#333] border-white/10 text-white placeholder:text-white/40 focus:border-[#FFEA00] focus:ring-[#FFEA00] min-h-[100px]"
              />
            </div>

            <Button
              type="submit"
              data-testid="continue-btn"
              disabled={loading}
              className="w-full bg-[#FFEA00] hover:bg-[#FFD600] text-black font-semibold py-6 text-lg rounded-lg transition-all hover:scale-[1.02]"
            >
              {loading ? t.common.saving : t.profile.continueBtn}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
